export interface VersusMenuActionState {
  pending: boolean;
  error: string;
  errorTimerSec: number;
  phase: "idle" | "pending" | "retrying_once" | "failed_ready_to_retry";
  activeAction: string | null;
  retryReadyAction: string | null;
  statusLine: string;
}

interface RunVersusMenuActionArgs<T> {
  state: VersusMenuActionState;
  actionKey: string;
  task: () => Promise<T>;
  onSuccess: (result: T) => void;
  onFailure?: (message: string) => void;
  fallbackError: string;
  enabled?: boolean;
  disabledReason?: string | null;
  errorDurationSec?: number;
  statusDurationSec?: number;
  shouldRetrySilently?: (error: unknown) => boolean;
}

function resolveErrorMessage(error: unknown, fallbackError: string): string {
  if (error instanceof Error && error.message.trim() !== "") {
    return error.message;
  }
  if (typeof error === "string" && error.trim() !== "") {
    return error;
  }
  return fallbackError;
}

function isTransientNetworkError(error: unknown): boolean {
  const message = resolveErrorMessage(error, "").toUpperCase();
  return (
    message.includes("TIMEOUT")
    || message.includes("NETWORK")
    || message.includes("OFFLINE")
    || message.includes("FETCH")
    || message.includes("ABORT")
  );
}

export async function runVersusMenuAction<T>({
  state,
  actionKey,
  task,
  onSuccess,
  onFailure,
  fallbackError,
  enabled = true,
  disabledReason = null,
  errorDurationSec = 3,
  statusDurationSec = 3,
  shouldRetrySilently = isTransientNetworkError
}: RunVersusMenuActionArgs<T>): Promise<void> {
  if (state.pending && state.activeAction === actionKey) {
    return;
  }

  if (!enabled) {
    state.error = disabledReason ?? fallbackError;
    state.errorTimerSec = errorDurationSec;
    state.phase = "failed_ready_to_retry";
    state.activeAction = actionKey;
    state.retryReadyAction = actionKey;
    state.statusLine = state.error;
    return;
  }

  state.activeAction = actionKey;
  state.retryReadyAction = null;
  state.pending = true;
  state.phase = "pending";
  state.error = "";
  state.errorTimerSec = 0;
  state.statusLine = "";

  let retriedOnce = false;

  while (true) {
    try {
      const result = await task();
      onSuccess(result);
      state.pending = false;
      state.phase = "idle";
      state.activeAction = null;
      state.retryReadyAction = null;
      return;
    } catch (error) {
      if (!retriedOnce && shouldRetrySilently(error)) {
        retriedOnce = true;
        state.phase = "retrying_once";
        state.statusLine = "RETRYING...";
        state.error = "";
        state.errorTimerSec = 0;
        continue;
      }
      const message = resolveErrorMessage(error, fallbackError);
      state.error = message;
      state.errorTimerSec = Math.max(errorDurationSec, statusDurationSec);
      state.statusLine = message;
      state.pending = false;
      state.phase = "failed_ready_to_retry";
      state.retryReadyAction = actionKey;
      if (onFailure != null) {
        onFailure(message);
      }
      return;
    }
  }
}
